/**
 * DynamoDB Service for Tic-Tac-Toe Game
 * Handles all database operations for game sessions, user profiles, and statistics
 */

import { 
  GameSession, 
  Profile, 
  RoomCode, 
  GameStatistics, 
  GameResult,
  GameId,
  PlayerId 
} from '../types';
import { AWS_CONFIG } from './config';

// DynamoDB client will be initialized when AWS SDK is available
let dynamoDBClient: any = null;
let docClient: any = null;

/**
 * Initialize DynamoDB clients
 */
export const initializeDynamoDB = async () => {
  try {
    // These imports will be available once AWS SDK is installed
    const { DynamoDBClient } = await import('@aws-sdk/client-dynamodb');
    const { DynamoDBDocumentClient } = await import('@aws-sdk/lib-dynamodb');
    
    dynamoDBClient = new DynamoDBClient({ region: AWS_CONFIG.region });
    docClient = DynamoDBDocumentClient.from(dynamoDBClient);
    
    console.log('DynamoDB clients initialized successfully');
    return true;
  } catch (error) {
    console.error('Failed to initialize DynamoDB clients:', error);
    return false;
  }
};

/**
 * Game Sessions Table Operations
 */
export class GameSessionsTable {
  private static tableName = AWS_CONFIG.dynamodb.gameSessionsTable;

  /**
   * Create a new game session
   */
  static async createGameSession(gameSession: GameSession): Promise<boolean> {
    if (!docClient) {
      console.error('DynamoDB client not initialized');
      return false;
    }

    try {
      const { PutCommand } = await import('@aws-sdk/lib-dynamodb');
      
      const command = new PutCommand({
        TableName: this.tableName,
        Item: {
          ...gameSession,
          createdAt: gameSession.createdAt.toISOString(),
          ttl: Math.floor(Date.now() / 1000) + (24 * 60 * 60), // 24 hours TTL
        },
        ConditionExpression: 'attribute_not_exists(id)'
      });

      await docClient.send(command);
      return true;
    } catch (error) {
      console.error('Error creating game session:', error);
      return false;
    }
  }

  /**
   * Get a game session by ID
   */
  static async getGameSession(gameId: GameId): Promise<GameSession | null> {
    if (!docClient) {
      console.error('DynamoDB client not initialized');
      return null;
    }

    try {
      const { GetCommand } = await import('@aws-sdk/lib-dynamodb');
      
      const command = new GetCommand({
        TableName: this.tableName,
        Key: { id: gameId }
      });

      const result = await docClient.send(command);
      
      if (result.Item) {
        return {
          ...result.Item,
          createdAt: new Date(result.Item.createdAt)
        } as GameSession;
      }
      
      return null;
    } catch (error) {
      console.error('Error getting game session:', error);
      return null;
    }
  }

  /**
   * Update a game session
   */
  static async updateGameSession(gameSession: GameSession): Promise<boolean> {
    if (!docClient) {
      console.error('DynamoDB client not initialized');
      return false;
    }

    try {
      const { PutCommand } = await import('@aws-sdk/lib-dynamodb');
      
      const command = new PutCommand({
        TableName: this.tableName,
        Item: {
          ...gameSession,
          createdAt: gameSession.createdAt.toISOString(),
          updatedAt: new Date().toISOString(),
        }
      });

      await docClient.send(command);
      return true;
    } catch (error) {
      console.error('Error updating game session:', error);
      return false;
    }
  }

  /**
   * Delete a game session
   */
  static async deleteGameSession(gameId: GameId): Promise<boolean> {
    if (!docClient) {
      console.error('DynamoDB client not initialized');
      return false;
    }

    try {
      const { DeleteCommand } = await import('@aws-sdk/lib-dynamodb');
      
      const command = new DeleteCommand({
        TableName: this.tableName,
        Key: { id: gameId }
      });

      await docClient.send(command);
      return true;
    } catch (error) {
      console.error('Error deleting game session:', error);
      return false;
    }
  }
}

/**
 * User Profiles Table Operations
 */
export class UserProfilesTable {
  private static tableName = AWS_CONFIG.dynamodb.userProfilesTable;

  /**
   * Create a new user profile
   */
  static async createUserProfile(profile: Profile): Promise<boolean> {
    if (!docClient) {
      console.error('DynamoDB client not initialized');
      return false;
    }

    try {
      const { PutCommand } = await import('@aws-sdk/lib-dynamodb');
      
      const command = new PutCommand({
        TableName: this.tableName,
        Item: {
          ...profile,
          createdAt: profile.createdAt.toISOString(),
        },
        ConditionExpression: 'attribute_not_exists(id)'
      });

      await docClient.send(command);
      return true;
    } catch (error) {
      console.error('Error creating user profile:', error);
      return false;
    }
  }

  /**
   * Get a user profile by ID
   */
  static async getUserProfile(userId: string): Promise<Profile | null> {
    if (!docClient) {
      console.error('DynamoDB client not initialized');
      return null;
    }

    try {
      const { GetCommand } = await import('@aws-sdk/lib-dynamodb');
      
      const command = new GetCommand({
        TableName: this.tableName,
        Key: { id: userId }
      });

      const result = await docClient.send(command);
      
      if (result.Item) {
        return {
          ...result.Item,
          createdAt: new Date(result.Item.createdAt)
        } as Profile;
      }
      
      return null;
    } catch (error) {
      console.error('Error getting user profile:', error);
      return null;
    }
  }

  /**
   * Update a user profile
   */
  static async updateUserProfile(profile: Profile): Promise<boolean> {
    if (!docClient) {
      console.error('DynamoDB client not initialized');
      return false;
    }

    try {
      const { PutCommand } = await import('@aws-sdk/lib-dynamodb');
      
      const command = new PutCommand({
        TableName: this.tableName,
        Item: {
          ...profile,
          createdAt: profile.createdAt.toISOString(),
          updatedAt: new Date().toISOString(),
        }
      });

      await docClient.send(command);
      return true;
    } catch (error) {
      console.error('Error updating user profile:', error);
      return false;
    }
  }

  /**
   * Get user profile by email (for authentication)
   */
  static async getUserProfileByEmail(email: string): Promise<Profile | null> {
    if (!docClient) {
      console.error('DynamoDB client not initialized');
      return null;
    }

    try {
      const { QueryCommand } = await import('@aws-sdk/lib-dynamodb');
      
      const command = new QueryCommand({
        TableName: this.tableName,
        IndexName: 'email-index', // Assumes GSI on email field
        KeyConditionExpression: 'email = :email',
        ExpressionAttributeValues: {
          ':email': email
        }
      });

      const result = await docClient.send(command);
      
      if (result.Items && result.Items.length > 0) {
        const item = result.Items[0];
        return {
          ...item,
          createdAt: new Date(item.createdAt)
        } as Profile;
      }
      
      return null;
    } catch (error) {
      console.error('Error getting user profile by email:', error);
      return null;
    }
  }
}

/**
 * Room Codes Table Operations
 */
export class RoomCodesTable {
  private static tableName = AWS_CONFIG.dynamodb.roomCodesTable;

  /**
   * Create a new room code
   */
  static async createRoomCode(roomCode: RoomCode): Promise<boolean> {
    if (!docClient) {
      console.error('DynamoDB client not initialized');
      return false;
    }

    try {
      const { PutCommand } = await import('@aws-sdk/lib-dynamodb');
      
      const command = new PutCommand({
        TableName: this.tableName,
        Item: {
          ...roomCode,
          expiresAt: roomCode.expiresAt.toISOString(),
          ttl: Math.floor(roomCode.expiresAt.getTime() / 1000), // TTL for automatic cleanup
        },
        ConditionExpression: 'attribute_not_exists(code)'
      });

      await docClient.send(command);
      return true;
    } catch (error) {
      console.error('Error creating room code:', error);
      return false;
    }
  }

  /**
   * Get a room code
   */
  static async getRoomCode(code: string): Promise<RoomCode | null> {
    if (!docClient) {
      console.error('DynamoDB client not initialized');
      return null;
    }

    try {
      const { GetCommand } = await import('@aws-sdk/lib-dynamodb');
      
      const command = new GetCommand({
        TableName: this.tableName,
        Key: { code }
      });

      const result = await docClient.send(command);
      
      if (result.Item) {
        const roomCode = {
          ...result.Item,
          expiresAt: new Date(result.Item.expiresAt)
        } as RoomCode;
        
        // Check if room code is expired
        if (roomCode.expiresAt < new Date()) {
          await this.deleteRoomCode(code);
          return null;
        }
        
        return roomCode;
      }
      
      return null;
    } catch (error) {
      console.error('Error getting room code:', error);
      return null;
    }
  }

  /**
   * Update room code with game ID
   */
  static async updateRoomCodeWithGameId(code: string, gameId: GameId): Promise<boolean> {
    if (!docClient) {
      console.error('DynamoDB client not initialized');
      return false;
    }

    try {
      const { UpdateCommand } = await import('@aws-sdk/lib-dynamodb');
      
      const command = new UpdateCommand({
        TableName: this.tableName,
        Key: { code },
        UpdateExpression: 'SET gameId = :gameId',
        ExpressionAttributeValues: {
          ':gameId': gameId
        },
        ConditionExpression: 'attribute_exists(code)'
      });

      await docClient.send(command);
      return true;
    } catch (error) {
      console.error('Error updating room code:', error);
      return false;
    }
  }

  /**
   * Delete a room code
   */
  static async deleteRoomCode(code: string): Promise<boolean> {
    if (!docClient) {
      console.error('DynamoDB client not initialized');
      return false;
    }

    try {
      const { DeleteCommand } = await import('@aws-sdk/lib-dynamodb');
      
      const command = new DeleteCommand({
        TableName: this.tableName,
        Key: { code }
      });

      await docClient.send(command);
      return true;
    } catch (error) {
      console.error('Error deleting room code:', error);
      return false;
    }
  }
}

/**
 * Game Statistics Table Operations
 */
export class GameStatisticsTable {
  private static tableName = AWS_CONFIG.dynamodb.gameStatisticsTable;

  /**
   * Record a game result
   */
  static async recordGameResult(userId: string, gameResult: GameResult): Promise<boolean> {
    if (!docClient) {
      console.error('DynamoDB client not initialized');
      return false;
    }

    try {
      const { UpdateCommand } = await import('@aws-sdk/lib-dynamodb');
      
      // Determine the statistics path based on game mode
      const isMultiplayer = gameResult.mode === 'multiplayer';
      const difficultyPath = gameResult.difficulty || 'easy';
      
      let updateExpression = 'ADD totalGames :one';
      const expressionAttributeValues: any = { ':one': 1 };
      
      if (isMultiplayer) {
        if (gameResult.winner === userId) {
          updateExpression += ', multiplayer.wins :one';
        } else if (gameResult.isDraw) {
          updateExpression += ', multiplayer.draws :one';
        } else {
          updateExpression += ', multiplayer.losses :one';
        }
      } else {
        const statPath = `singlePlayer.${difficultyPath}`;
        if (gameResult.winner === userId) {
          updateExpression += `, ${statPath}.wins :one`;
        } else if (gameResult.isDraw) {
          updateExpression += `, ${statPath}.draws :one`;
        } else {
          updateExpression += `, ${statPath}.losses :one`;
        }
      }
      
      const command = new UpdateCommand({
        TableName: this.tableName,
        Key: { userId },
        UpdateExpression: updateExpression,
        ExpressionAttributeValues: expressionAttributeValues
      });

      await docClient.send(command);
      return true;
    } catch (error) {
      console.error('Error recording game result:', error);
      return false;
    }
  }

  /**
   * Get user statistics
   */
  static async getUserStatistics(userId: string): Promise<GameStatistics | null> {
    if (!docClient) {
      console.error('DynamoDB client not initialized');
      return null;
    }

    try {
      const { GetCommand } = await import('@aws-sdk/lib-dynamodb');
      
      const command = new GetCommand({
        TableName: this.tableName,
        Key: { userId }
      });

      const result = await docClient.send(command);
      
      if (result.Item) {
        return result.Item as GameStatistics;
      }
      
      // Return default statistics if none exist
      return {
        singlePlayer: {
          easy: { wins: 0, losses: 0, draws: 0 },
          medium: { wins: 0, losses: 0, draws: 0 },
          hard: { wins: 0, losses: 0, draws: 0 }
        },
        multiplayer: { wins: 0, losses: 0, draws: 0 },
        totalGames: 0,
        winRate: 0
      };
    } catch (error) {
      console.error('Error getting user statistics:', error);
      return null;
    }
  }
}